import { cn, focusRing } from "@simpro/ui";
import { CircleCheck } from "lucide-react";
import { Link } from "react-router";
import { useSalonMoney } from "@/features/salon";
import { pill } from "@/shared/styles/pill";

/** A plan the salon wrote for its page (design note salon-home-landing.md 7b). */
export interface Plan {
  name: string;
  price: string;
  period: "month" | "visit";
  highlighted: boolean;
  lines: readonly { label: string; price: string | null }[];
}

/**
 * The salon's plans, in place of the menu's price cards when it chose plans (TDD-SA FR-SA-191): the
 * reference's cards with a name, what each includes, and a price a month or a visit. Information only:
 * nothing is sold or charged online (TDD-SA D6), so the button starts an ordinary booking. A
 * highlighted plan stands out in the raised colour with an accent edge.
 */
export function PlanCards({ plans }: { plans: readonly Plan[] }) {
  const money = useSalonMoney();

  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:gap-5">
      {plans.map((plan) => (
        <li
          key={plan.name}
          className={cn(
            "flex flex-col gap-5 rounded-[1.5rem] border p-5 lg:p-7",
            plan.highlighted ? "border-accent-500 bg-raised" : "border-edge bg-surface",
          )}
        >
          <h3 className="text-xl font-bold text-heading lg:text-2xl">{plan.name}</h3>
          {plan.lines.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {plan.lines.map((line) => (
                <li key={line.label} className="flex items-center gap-3">
                  <CircleCheck className="size-4.5 shrink-0 text-muted" aria-hidden />
                  <span className="min-w-0 flex-1 text-heading">{line.label}</span>
                  {line.price ? <span className="font-semibold text-heading">from {money(line.price)}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-auto flex items-end justify-between gap-3 border-t border-edge-subtle pt-4">
            <p className="text-sm text-subtle">
              <span className="headline text-[2rem] leading-none text-highlight">{money(plan.price)}</span>
              {plan.period === "month" ? " a month" : " a visit"}
            </p>
            <Link
              to="/book"
              className={cn(pill.base, pill.sm, plan.highlighted ? pill.ink : pill.outline, focusRing)}
            >
              Book a visit
              <span className="sr-only"> on the {plan.name} plan</span>
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}
